/**
 * Installs the chunked cache as transformers.js's storage backend.
 *
 * Without this, weights over 256 MiB cannot be cached at all (see
 * chunkedCache.ts for the measurement), which meant the default model's
 * 469 MB of weights re-downloaded on every single visit while the page
 * cheerfully claimed otherwise.
 *
 * Called from both workers. Each has its own module scope, so each installs
 * once into its own copy of `env`.
 */

import { env } from "@huggingface/transformers";
import { ChunkedCache, CHUNK_ORIGIN, MANIFEST_HEADER } from "./chunkedCache";

/**
 * Deliberately not transformers.js's default `transformers-cache`.
 *
 * A distinct name means entries written by the stock backend are never
 * confused with chunked ones, and it gives the UI a single thing to clear
 * when a visitor wants their disk back.
 */
export const WEIGHT_CACHE = "the-mind-weights";

/** The stock name, kept only so the clear button can sweep up older visits. */
const LEGACY_CACHE = "transformers-cache";

let installing: Promise<void> | null = null;

export function installWeightCache(): Promise<void> {
  installing ??= (async () => {
    // In dev, fetch weights through the local mirror instead of hitting
    // Hugging Face on every cache-clearing test cycle. The plugin serves from
    // disk and falls through to HF on a miss, so the first run populates it.
    // import.meta.env.DEV is false in any production build.
    if (import.meta.env.DEV) {
      env.remoteHost = `${location.origin}/hf/`;
      console.info("[the-mind] dev weight mirror active at /hf/");
    }

    if (typeof caches === "undefined") return; // no Cache API, nothing to install
    try {
      env.useCustomCache = true;
      env.customCache = await ChunkedCache.open(WEIGHT_CACHE);
    } catch (err) {
      // A failure here must not stop the model loading. Worst case we are
      // back to the old behaviour of re-downloading, which is slow, not broken.
      env.useCustomCache = false;
      console.warn("[the-mind] chunked weight cache unavailable, falling back:", err);
    }
  })();
  return installing;
}

export type CacheUsage = { usageBytes: number; quotaBytes: number; freeBytes: number };

export async function cacheUsage(): Promise<CacheUsage> {
  const est = await navigator.storage?.estimate?.();
  const usageBytes = est?.usage ?? 0;
  const quotaBytes = est?.quota ?? 0;
  return { usageBytes, quotaBytes, freeBytes: Math.max(0, quotaBytes - usageBytes) };
}

/**
 * Storage quota is NOT a constant, and must not be designed against as one.
 *
 * The browser derives it from free disk space on the partition holding its
 * profile, so it varies per visitor and per machine. Measured on one machine
 * on 2026-07-26: the Electron pane reported 5.96 GB while Chrome on the same
 * box reported 11.25 GB, because their profiles sit on different drives.
 *
 * I briefly treated the 5.96 GB figure as a property of the site rather than
 * of my own measurement environment. It is not. Hence this check: ask what is
 * actually left before committing to a download, rather than assuming a
 * ceiling that will be wrong for most visitors in one direction or the other.
 */
export type FitVerdict = {
  fits: boolean;
  freeBytes: number;
  quotaBytes: number;
  /** Set when the download would not fit, phrased for a visitor. */
  warning?: string;
};

const HEADROOM = 1.15; // weights plus tokenizer, and quotas shrink as disks fill

export async function willFit(bytes: number): Promise<FitVerdict> {
  const { quotaBytes, freeBytes } = await cacheUsage();
  if (!quotaBytes) return { fits: true, freeBytes: 0, quotaBytes: 0 };

  const needed = bytes * HEADROOM;
  if (needed <= freeBytes) return { fits: true, freeBytes, quotaBytes };

  const gb = (n: number) => (n / 1e9).toFixed(1) + " GB";
  return {
    fits: false,
    freeBytes,
    quotaBytes,
    warning:
      `This needs about ${gb(needed)} and your browser has ${gb(freeBytes)} left ` +
      `of a ${gb(quotaBytes)} allowance. Clearing the cache will make room; ` +
      `otherwise the browser may evict a model you already downloaded.`,
  };
}

/** Drop every cached weight, both chunked and anything an earlier visit left. */
export async function clearWeightCache(): Promise<void> {
  if (typeof caches === "undefined") return;
  await Promise.all([caches.delete(WEIGHT_CACHE), caches.delete(LEGACY_CACHE)]);
}

/**
 * Which model repos actually have bytes on this device.
 *
 * Free asked the lab to show "storage usage and models loaded". Loaded is the
 * interesting half: the size a model claims in the catalogue is what it will
 * COST, and this is what has already been paid. The difference decides whether
 * picking a tier means a two second warm start or a two gigabyte download, and
 * that is the single most useful thing to know before pressing run.
 *
 * Matches on the Hugging Face repo id appearing in a cached request URL, which
 * is how transformers.js names every weight it fetches. Counts bytes per repo
 * as well, since a partially cached model is a real state: a load can be
 * interrupted and what survives is a repo with some of its files.
 *
 * The chunked entries are counted too. `chunkedCache` stores oversized bodies
 * under a synthetic `mind-chunk.invalid` origin carrying the encoded real URL,
 * so the repo id is still in the key and still matches.
 */
export async function cachedRepos(): Promise<Record<string, { files: number; bytes: number }>> {
  const out: Record<string, { files: number; bytes: number }> = {};
  if (typeof caches === "undefined") return out;

  // DEDUPE ACROSS CACHES. The same file can sit in both the current cache and
  // the legacy transformers one, and counting it twice made the totals come to
  // 4.87 GB against a reported 2.33 GB of storage used. Near enough to exactly
  // double to be the giveaway.
  const seen = new Set<string>();

  const add = (repo: string, bytes: number) => {
    const cur = out[repo] ?? { files: 0, bytes: 0 };
    out[repo] = { files: cur.files + 1, bytes: cur.bytes + (Number.isFinite(bytes) ? bytes : 0) };
  };

  for (const name of [WEIGHT_CACHE, LEGACY_CACHE]) {
    let cache: Cache;
    try {
      cache = await caches.open(name);
    } catch {
      continue;
    }
    for (const req of await cache.keys()) {
      // SKIP THE CHUNKS. A chunked body is stored as N pieces under a synthetic
      // origin plus one manifest under the real URL. The pieces carry no
      // content-length, so counting them adds nothing and counting them AND the
      // manifest would double. The manifest knows the true size; ask it.
      if (req.url.startsWith(CHUNK_ORIGIN)) continue;
      if (seen.has(req.url)) continue;
      seen.add(req.url);

      let url = req.url;
      try {
        url = decodeURIComponent(url);
      } catch {
        /* a bad escape is not worth failing the whole scan */
      }
      const m = /huggingface\.co\/([^/]+\/[^/]+)\//.exec(url) ?? /\/hf\/([^/]+\/[^/]+)\//.exec(url);
      if (!m) continue;

      const res = await cache.match(req);
      if (!res) continue;

      if (res.headers.get(MANIFEST_HEADER) === "1") {
        // The manifest is where a 469 MB file declares itself, and reading it is
        // the difference between reporting "cached 21 MB" and the truth.
        //
        // BUT A MANIFEST CAN OUTLIVE ITS CHUNKS. The browser evicts entries
        // under quota pressure and has no idea these belong together, so a
        // manifest claiming 2.8 GB can sit on top of nothing. Trusting it made
        // the totals come to 4.87 GB against 2.33 GB of storage actually used.
        //
        // So verify. `match` on each chunk key is a lookup, not a read, so this
        // costs a handful of index hits and not the gigabytes themselves.
        try {
          const meta = (await res.clone().json()) as { size?: number; chunks?: number };
          const want = Number(meta?.chunks ?? 0);
          let present = 0;
          for (let i = 0; i < want; i++) {
            if (await cache.match(`${CHUNK_ORIGIN}/${encodeURIComponent(req.url)}/${i}`)) present++;
          }
          // Partial is reported as partial rather than rounded up to whole. A
          // model missing one chunk will re-download in full, so counting it as
          // cached would tell the operator the opposite of what will happen.
          add(m[1], present === want ? Number(meta?.size ?? 0) : 0);
        } catch {
          add(m[1], 0);
        }
        continue;
      }

      add(m[1], Number(res.headers.get("content-length") ?? 0));
    }
  }
  return out;
}
